import { CartAddEvent, ThemeEvents } from "@theme/events";
import { DialogOpenEvent } from "@theme/dialog";
import {
  RESERVE_STORAGE_KEY,
  afterCartChange,
  afterOpen,
  afterPageLoad,
  formatClock,
  overrideDurationMs,
  parseReservation,
  remainingMs,
} from "@theme/cart-reserve-clock";

/**
 * SLEEPSAKE (DEV-835): the reserved cart's clock and countdown bar
 * (`snippets/cart-reserve-bar.liquid`). Loaded only where the reserved cart is
 * on (`snippets/cart-reserved-gate.liquid`), on every page including /cart so
 * a change there restarts the clock, and from outside the drawer, because
 * morph never runs a script it inserts into a drawer first rendered empty.
 */

/** @typedef {import("@theme/cart-reserve-clock").Reservation} Reservation */

const DURATION_MS =
  (Number(document.querySelector("script[data-cart-reserve-minutes]")?.getAttribute("data-cart-reserve-minutes")) ||
    7) * 60_000;

/** Without storage, the reservation this page view runs on. */
let memoryReservation = /** @type {Reservation | null} */ (null);

/** @returns {Reservation | null} */
function loadReservation() {
  try {
    return parseReservation(localStorage.getItem(RESERVE_STORAGE_KEY)) ?? memoryReservation;
  } catch {
    return memoryReservation;
  }
}

/** @param {Reservation} reservation */
function saveReservation(reservation) {
  memoryReservation = reservation;
  try {
    localStorage.setItem(RESERVE_STORAGE_KEY, JSON.stringify(reservation));
  } catch {
    // Storage blocked: the clock still runs for this page view.
  }
}

/**
 * @param {(current: Reservation | null, options: { now: number, durationMs: number }) => Reservation} step
 * @param {number} [durationMs]
 */
function applyStep(step, durationMs = DURATION_MS) {
  const current = loadReservation();
  const next = step(current, { now: Date.now(), durationMs });
  if (next !== current) saveReservation(next);
}

if (afterPageLoad(loadReservation(), Date.now()) === null) {
  memoryReservation = null;
  try {
    localStorage.removeItem(RESERVE_STORAGE_KEY);
  } catch {
    // Storage blocked: nothing stored to clear.
  }
}

// `?sk_reserve=20s`: a fresh short clock, once per page load.
const overrideMs = overrideDurationMs(location.search);
if (overrideMs !== null) saveReservation({ startedAt: Date.now(), durationMs: overrideMs });

/**
 * A change the shopper made: an add (product page, cart upsell, quick order)
 * or a quantity change or removal in the cart. Not the bonus gift card's own
 * reconcile, which fires on every drawer open and would hold the clock at
 * its full length.
 * @param {Event & { detail?: { data?: { didError?: boolean, source?: string } } }} event
 */
function isShopperChange(event) {
  if (event.detail?.data?.didError) return false;
  return event instanceof CartAddEvent || event.detail?.data?.source === "cart-items-component";
}

// At module level, not on the element: an add made while the drawer is empty,
// or on /cart where there is no drawer, has no bar in the page to hear it.
document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  if (isShopperChange(event)) applyStep(afterCartChange);
});

class CartReserveBar extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  #interval = 0;

  #shown = "";

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    // Registered after the module's add handler, so it renders the clock that
    // handler just started.
    document.addEventListener(ThemeEvents.cartUpdate, this.#render, { signal });
    this.closest("cart-drawer-component")?.addEventListener(
      DialogOpenEvent.eventName,
      () => {
        applyStep(afterOpen);
        this.#render();
      },
      { signal },
    );

    // Deferred: during an add, morph can connect this bar before the module's
    // add handler has run, and the bar would flash the stale clock.
    queueMicrotask(this.#render);
    this.#interval = window.setInterval(this.#render, 1000);
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    window.clearInterval(this.#interval);
  }

  #render = () => {
    if (!this.isConnected) return;
    const reservation = loadReservation();
    // No clock yet: the server-rendered full length stands until an add or an open starts one.
    const left = reservation ? remainingMs(reservation, Date.now()) : DURATION_MS;
    const shown = left === 0 ? "expired" : formatClock(left);
    if (shown === this.#shown) return;
    this.#shown = shown;

    // State lives inside the morph-skipped subtree: morph resets the host's
    // own attributes on every cart change.
    const bar = this.querySelector(".cart-reserve");
    const text = this.querySelector(".cart-reserve__text");
    if (!bar || !text) return;
    bar.classList.toggle("cart-reserve--expired", left === 0);
    if (left === 0) {
      text.textContent = this.getAttribute("expired-text") ?? "";
    } else {
      text.replaceChildren(...this.#lineWithClock(shown));
    }
  };

  /**
   * The countdown line with `[time]` swapped for the clock, built from nodes
   * so merchant text is never parsed as HTML.
   * @param {string} clock
   */
  #lineWithClock(clock) {
    const parts = (this.getAttribute("text") ?? "").split("[time]");
    /** @type {Node[]} */
    const nodes = [];
    parts.forEach((part, index) => {
      if (index > 0) {
        const time = document.createElement("strong");
        time.className = "cart-reserve__time";
        time.textContent = clock;
        nodes.push(time);
      }
      if (part) nodes.push(document.createTextNode(part));
    });
    return nodes;
  }
}

if (!customElements.get("cart-reserve-bar")) {
  customElements.define("cart-reserve-bar", CartReserveBar);
}
